import React, { useState, useMemo, useEffect } from 'react';
import { ArrowLeft, Check, Clock, ExternalLink, Facebook, FileText, Link, Linkedin, Mic, Play, Share2, Twitter } from 'lucide-react';
import { Reveal, ReportCard } from '../components';
import { useReportContext } from '../contexts';
import { useIntelligence } from '../hooks';

const TABS = [
  { id: 'all', label: 'ALL', icon: FileText },
  { id: 'REPORTS', label: 'REPORTS', icon: FileText },
  { id: 'PODCASTS', label: 'PODCASTS', icon: Mic },
  { id: 'VIDEOS', label: 'VIDEOS', icon: Play },
];

const getArticleShareId = (report) => report?.slug || report?.id || '';

const getArticleShareUrl = (report) => {
  if (typeof window === 'undefined') return '';
  const shareId = getArticleShareId(report);
  const url = new URL('/intelligence', window.location.origin);
  if (shareId) url.searchParams.set('article', shareId);
  return url.toString();
};

export const IntelligencePage = () => {
  const { currentReport, clearReport, viewReport } = useReportContext();
  const [activeTab, setActiveTab] = useState('all');
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const [hasCopiedShareUrl, setHasCopiedShareUrl] = useState(false);
  const { reports, isLoading } = useIntelligence();
  const shareUrl = getArticleShareUrl(currentReport);
  const encodedShareUrl = encodeURIComponent(shareUrl);
  const encodedShareTitle = encodeURIComponent(currentReport?.title || 'Panometra Intelligence');

  const filteredReports = useMemo(() => {
    if (activeTab === 'all') return reports;
    return reports.filter(report => {
      if (activeTab === 'REPORTS') return report.category === 'PERSPECTIVE' || report.category === 'REPORT';
      if (activeTab === 'PODCASTS') return report.category === 'PODCAST' || report.mediaType === 'podcast';
      if (activeTab === 'VIDEOS') return report.category === 'VIDEO' || report.mediaType === 'video';
      return true;
    });
  }, [activeTab, reports]);

  useEffect(() => {
    if (typeof window === 'undefined' || currentReport || !reports.length) return;

    const articleId = new URLSearchParams(window.location.search).get('article');
    if (!articleId) return;

    const matchingReport = reports.find((report) => getArticleShareId(report) === articleId);
    if (matchingReport) viewReport(matchingReport);
  }, [currentReport, reports, viewReport]);

  useEffect(() => {
    setIsShareMenuOpen(false);
    setHasCopiedShareUrl(false);
  }, [currentReport?.id]);

  const handleClearReport = () => {
    clearReport();
    setIsShareMenuOpen(false);

    if (typeof window !== 'undefined' && window.location.pathname === '/intelligence' && window.location.search) {
      window.history.pushState({ page: 'intelligence', companyId: null }, '', '/intelligence');
    }
  };

  const copyShareUrl = async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.setAttribute('readonly', '');
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }

    setHasCopiedShareUrl(true);
  };

  if (currentReport) {
    return (
      <div className="min-h-screen bg-[#fcfaf7] pt-20 pb-16 selection:bg-zinc-200 sm:pt-24 sm:pb-24">
        <article className="mx-auto max-w-4xl px-4 sm:px-6">
          {/* Back Navigation */}
          <button
            onClick={handleClearReport}
            className="group mb-8 flex items-center gap-2 text-zinc-400 transition-colors hover:text-zinc-900 sm:mb-12 font-mono text-[10px] font-bold uppercase tracking-[0.3em]"
          >
            <ArrowLeft size={12} className="group-hover:-translate-x-1 transition-transform" />
            Archive
          </button>

          {/* Editorial Header / Masthead */}
          <Reveal className="relative z-30">
            <div className="mb-8 flex flex-col sm:mb-10">
              <div className="mb-6 flex items-center justify-between gap-4 border-y border-zinc-200 py-3 sm:mb-8">
                <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-zinc-900">
                  {currentReport.category}
                </span>
                <span className="shrink-0 font-mono text-[10px] uppercase tracking-widest text-zinc-400">{currentReport.date}</span>
              </div>
              
              <h1 className="mb-7 text-[2.45rem] font-bold leading-[0.98] tracking-tight text-zinc-900 text-balance sm:mb-8 sm:text-5xl md:text-6xl md:leading-[0.9]">
                {currentReport.title}
              </h1>

              <div className="mb-8 flex flex-col items-stretch gap-4 border-b border-zinc-200 pb-6 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 sm:mb-12 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-8 sm:gap-y-4 sm:pb-8">
                <span className="flex items-center gap-2"><Clock size={12} /> {currentReport.readTime}</span>
                <span className="hidden text-zinc-300 sm:inline">/</span>
                <span>By {currentReport.author}</span>
                <span className="hidden text-zinc-300 sm:inline">/</span>
                <div className="relative w-full sm:ml-auto sm:w-auto">
                  <button
                    type="button"
                    onClick={() => setIsShareMenuOpen((isOpen) => !isOpen)}
                    className="flex h-11 w-full items-center justify-center gap-2 border border-zinc-200 bg-white/70 px-4 font-bold uppercase tracking-[0.2em] text-zinc-900 transition-colors hover:text-zinc-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2 focus-visible:ring-offset-[#fcfaf7] sm:h-auto sm:w-auto sm:justify-start sm:border-0 sm:bg-transparent sm:px-0"
                    aria-expanded={isShareMenuOpen}
                    aria-haspopup="menu"
                  >
                    <Share2 size={12} /> Share Editorial
                  </button>

                  {isShareMenuOpen && (
                    <div
                      className="absolute left-0 top-full z-50 mt-3 w-full border border-zinc-200 bg-white py-2 text-left font-sans shadow-xl shadow-zinc-900/10 sm:left-auto sm:right-0 sm:w-56"
                      role="menu"
                    >
                      <button
                        type="button"
                        onClick={copyShareUrl}
                        className="flex w-full items-center gap-3 px-4 py-3 text-xs font-medium normal-case tracking-normal text-zinc-600 transition-colors hover:bg-zinc-50 hover:text-zinc-950"
                        role="menuitem"
                      >
                        {hasCopiedShareUrl ? <Check size={14} /> : <Link size={14} />}
                        {hasCopiedShareUrl ? 'Copied link' : 'Copy link'}
                      </button>
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodedShareUrl}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-3 px-4 py-3 text-xs font-medium normal-case tracking-normal text-zinc-600 transition-colors hover:bg-zinc-50 hover:text-zinc-950"
                        role="menuitem"
                      >
                        <Facebook size={14} />
                        Share on Facebook
                      </a>
                      <a
                        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedShareUrl}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-3 px-4 py-3 text-xs font-medium normal-case tracking-normal text-zinc-600 transition-colors hover:bg-zinc-50 hover:text-zinc-950"
                        role="menuitem"
                      >
                        <Linkedin size={14} />
                        Share on LinkedIn
                      </a>
                      <a
                        href={`https://twitter.com/intent/tweet?url=${encodedShareUrl}&text=${encodedShareTitle}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-3 px-4 py-3 text-xs font-medium normal-case tracking-normal text-zinc-600 transition-colors hover:bg-zinc-50 hover:text-zinc-950"
                        role="menuitem"
                      >
                        <Twitter size={14} />
                        Share on X
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Reveal>

          {(currentReport.mediaUrl || currentReport.sourceUrl) && (
            <Reveal delay={150}>
              <div className="mb-10 flex flex-col gap-3 sm:mb-12 sm:flex-row sm:flex-wrap">
                {currentReport.mediaUrl && (
                  <a
                    href={currentReport.mediaUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-3 bg-zinc-900 px-5 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-zinc-700"
                  >
                    <Play size={14} />
                    {currentReport.mediaType === 'video' ? 'Watch' : currentReport.mediaType === 'podcast' ? 'Listen' : 'Open media'}
                  </a>
                )}
                {currentReport.sourceUrl && (
                  <a
                    href={currentReport.sourceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-3 border border-zinc-300 px-5 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors hover:border-zinc-900"
                  >
                    <ExternalLink size={14} />
                    Source
                  </a>
                )}
              </div>
            </Reveal>
          )}

          {/* Lead Image */}
          <Reveal delay={200} className="relative z-0">
            <div className="mb-12 aspect-[4/3] w-full overflow-hidden bg-zinc-100 grayscale transition-all duration-1000 hover:grayscale-0 sm:mb-20 sm:aspect-[21/9]">
              <img 
                src={currentReport.image} 
                alt={currentReport.title} 
                className="w-full h-full object-cover scale-105 hover:scale-100 transition-transform duration-[2s]"
              />
            </div>
          </Reveal>

          {/* Body Content */}
          <div className="mx-auto max-w-2xl">
            <div className="prose prose-zinc max-w-none font-sans">
              {currentReport.content.map((block, index) => {
                return (
                  <Reveal key={index} delay={index * 50}>
                    {block.type === 'h3' && (
                      <h3
                        className="mt-14 mb-6 border-t border-zinc-100 pt-10 text-[12px] font-bold uppercase leading-relaxed tracking-[0.28em] text-zinc-900 sm:mt-20 sm:mb-8 sm:pt-12 sm:text-[14px] sm:tracking-[0.3em]"
                        dangerouslySetInnerHTML={{ __html: block.text }}
                      />
                    )}
                    {block.type === 'p' && (
                      <p
                        className={`mb-8 leading-relaxed text-zinc-800 sm:mb-10 ${
                        index === 0 
                          ? 'mb-12 text-xl font-medium leading-[1.45] text-zinc-900 sm:mb-16 sm:text-2xl sm:leading-[1.35] md:text-3xl md:leading-[1.3]' 
                          : 'text-lg sm:text-xl'
                      }`}
                        dangerouslySetInnerHTML={{ __html: block.text }}
                      />
                    )}
                    {block.type === 'quote' && (
                      <div className="relative my-12 overflow-visible border-y border-zinc-900 px-4 py-7 sm:my-20 sm:px-12 sm:py-8">
                        <blockquote
                          className="relative z-10 text-center text-2xl italic leading-tight text-zinc-900 sm:text-3xl md:text-4xl font-medium"
                          dangerouslySetInnerHTML={{ __html: `&quot;${block.text}&quot;` }}
                        />
                        <div className="pointer-events-none absolute left-1/2 top-1/2 -z-0 -translate-x-1/2 -translate-y-1/2 select-none font-mono text-[8rem] font-bold text-zinc-100 opacity-50 sm:text-[12rem]">
                          "
                        </div>
                      </div>
                    )}
                    {block.type === 'image' && (
                      <figure className="my-10 sm:my-14">
                        <img src={block.url} alt="" className="w-full object-cover" />
                      </figure>
                    )}
                    {block.type === 'html' && (
                      <div
                        className="mb-8 text-lg leading-relaxed text-zinc-800 sm:mb-10 sm:text-xl [&_blockquote]:border-l-4 [&_blockquote]:border-zinc-900 [&_blockquote]:pl-5 [&_blockquote]:italic [&_hr]:my-10 [&_hr]:border-zinc-200 sm:[&_hr]:my-12 [&_li]:mb-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_pre]:overflow-auto [&_pre]:bg-zinc-950 [&_pre]:p-4 [&_pre]:text-zinc-50 sm:[&_pre]:p-5 [&_ul]:list-disc [&_ul]:pl-6"
                        dangerouslySetInnerHTML={{ __html: block.html }}
                      />
                    )}
                  </Reveal>
                );
              })}
              {currentReport.content.length === 0 && (
                <p className="mb-10 text-lg leading-relaxed text-zinc-500 sm:text-xl">
                  This intelligence item links to external media.
                </p>
              )}
            </div>

            {/* Footer Sign-off */}
            <div className="mt-20 flex flex-col items-center gap-7 border-t-[3px] border-zinc-900 pt-12 sm:mt-32 sm:gap-8 sm:pt-16">
              <div className="flex h-12 w-12 rotate-45 items-center justify-center bg-zinc-900 font-mono text-xl text-white transition-transform duration-500 hover:rotate-0">
                P
              </div>
              <div className="text-center font-mono text-[10px] uppercase tracking-[0.35em] text-zinc-400 sm:tracking-[0.5em]">
                Panometra Intelligence Archive // MMXXVI
              </div>
              <button 
                onClick={handleClearReport}
                className="group mt-4 flex items-center gap-3 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-900 transition-colors hover:text-zinc-500 sm:mt-8"
              >
                <ArrowLeft size={14} className="group-hover:-translate-x-2 transition-transform" />
                Return to Archive
              </button>
            </div>
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fcfaf7] pt-20 sm:pt-24 pb-24 text-zinc-900 font-sans">
      {/* Tabs / Filtering */}
      <div className="sticky top-16 sm:top-20 z-30 bg-[#fcfaf7]/80 backdrop-blur-md border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-12 py-6">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group relative flex items-center gap-2 py-2 text-[10px] font-bold tracking-[0.2em] transition-all
                  ${activeTab === tab.id ? 'text-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}
                `}
              >
                <tab.icon size={12} className={activeTab === tab.id ? 'text-zinc-900' : 'text-zinc-400'} />
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute -bottom-6 left-0 w-full h-0.5 bg-zinc-900 animate-in fade-in slide-in-from-bottom-1 duration-500" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-6 pt-12 pb-20">
        {isLoading && (
          <p className="mb-8 text-center text-zinc-400 font-mono text-xs tracking-[0.2em] uppercase">
            Loading intelligence...
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0.5 bg-zinc-200 border border-zinc-200">
          {filteredReports.map((report, index) => (
            <div key={report.id} className="bg-[#fcfaf7]">
              <ReportCard
                report={report}
                index={index}
                onClick={() => viewReport(report)}
              />
            </div>
          ))}
          {filteredReports.length === 0 && (
            <div className="col-span-full py-40 text-center bg-[#fcfaf7]">
              <p className="text-zinc-400 font-mono text-sm tracking-widest uppercase">No reports found in this category.</p>
            </div>
          )}
        </div>
      </div>

      {/* Newsletter / Footer Section */}
      <div className="max-w-7xl mx-auto px-6 py-40 border-t border-zinc-200 text-center">
        <Reveal>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight mb-8 font-sans">Stay informed.</h2>
          <p className="text-zinc-500 max-w-lg mx-auto mb-12 text-lg">
            Subscribe to our weekly briefing on the future of autonomous intelligence and architectural systems.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="YOUR EMAIL"
              className="w-full bg-white border border-zinc-200 px-6 py-4 text-sm font-bold tracking-widest focus:outline-none focus:border-zinc-900 transition-colors uppercase"
            />
            <button className="w-full sm:w-auto bg-zinc-900 text-white px-8 py-4 text-xs font-bold tracking-widest hover:bg-zinc-800 transition-all uppercase whitespace-nowrap">
              Subscribe
            </button>
          </div>
        </Reveal>
      </div>
    </div>
  );
};
